import { useState } from "react";
import { signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, OAuthProvider, sendPasswordResetEmail } from "firebase/auth";
import { auth } from "./firebase";
import emailjs from "@emailjs/browser";

const EMAILJS_SERVICE_ID = "service_6s4y6ci";
const EMAILJS_WELCOME_TEMPLATE = "template_w544ezp";
const EMAILJS_PUBLIC_KEY = "IqticZ4G35CQ30ugZ";

export default function Login({ onLogin, onSignup }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [view, setView] = useState("login");
  const [resetSent, setResetSent] = useState(false);

  async function handleLogin() {
    setError("");
    if (!email || !password) { setError("Please fill in all fields."); return; }
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      onLogin();
    } catch (err) {
      if (err.code === "auth/user-not-found" || err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
        setError("Invalid email or password.");
      } else if (err.code === "auth/invalid-email") {
        setError("Please enter a valid email address.");
      } else {
        setError("Sign in failed. Please try again.");
      }
    }
    setLoading(false);
  }

  async function handleGoogle() {
    setError(""); setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const isNewUser = result._tokenResponse?.isNewUser;
      if (!isNewUser) {
        try {
          await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_WELCOME_TEMPLATE, {
            to_name: user.displayName || user.email?.split("@")[0] || "Music Lover",
            to_email: user.email,
            from_name: "MyMusic Team",
            message: "Welcome back to MyMusic! Your playlists and liked songs are waiting for you.",
            app_link: "https://mymusicstream.online",
            company_name: "MyMusic",
          }, EMAILJS_PUBLIC_KEY);
        } catch {}
      }
      onLogin();
    } catch { setError("Google sign-in failed. Please try again."); }
    setLoading(false);
  }

  async function handleApple() {
    setError(""); setLoading(true);
    try {
      const provider = new OAuthProvider("apple.com");
      provider.addScope("email"); provider.addScope("name");
      await signInWithPopup(auth, provider);
      onLogin();
    } catch { setError("Apple sign-in failed. Please try again."); }
    setLoading(false);
  }

  async function handleReset() {
    setError("");
    if (!email) { setError("Enter your email address first."); return; }
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
    } catch (err) {
      if (err.code === "auth/user-not-found") setError("No account found with that email.");
      else setError("Failed to send reset email. Try again.");
    }
    setLoading(false);
  }

  if (view === "reset") {
    return (
      <div style={S.page}>
        <style>{CSS}</style>
        <div style={S.card}>
          <Logo />
          {resetSent ? (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#000", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                <svg viewBox="0 0 24 24" style={{ width: 22, height: 22, fill: "none", stroke: "#fff", strokeWidth: 2 }}><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <h2 style={{ fontFamily: "'Instrument Serif', serif", fontSize: 28, marginBottom: 12 }}>Check your inbox</h2>
              <p style={{ fontSize: 14, color: "#888", lineHeight: 1.7, marginBottom: 32 }}>We sent a password reset link to<br /><strong style={{ color: "#000" }}>{email}</strong></p>
              <button onClick={() => { setView("login"); setResetSent(false); }} style={S.btnOutline}>Back to sign in</button>
            </div>
          ) : (
            <>
              <h1 style={S.heading}>Reset your<br />password.</h1>
              <p style={S.sub}>Enter your email and we'll send you a reset link.</p>
              {error && <div style={S.errorBox}>{error}</div>}
              <Field label="Email address">
                <input style={S.input} type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && handleReset()} className="auth-input" autoFocus />
              </Field>
              <button onClick={handleReset} disabled={loading} style={{ ...S.btnDark, marginTop: 8, marginBottom: 16 }}>
                {loading ? "Sending..." : "Send reset link"}
              </button>
              <button onClick={() => { setView("login"); setError(""); }} style={S.btnGhost}>← Back to sign in</button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <style>{CSS}</style>
      <div style={S.left}>
        <div style={S.card}>
          <Logo />
          <h1 style={S.heading}>Welcome<br />back.</h1>
          <p style={S.sub}>Sign in to continue your music journey.</p>
          {error && <div style={S.errorBox}>{error}</div>}
          <button onClick={handleGoogle} disabled={loading} style={S.socialBtn} className="social-btn">
            <GoogleSVG />
            <span>Continue with Google</span>
          </button>
          <button onClick={handleApple} disabled={loading} style={{ ...S.socialBtn, marginBottom: 28 }} className="social-btn">
            <AppleSVG />
            <span>Continue with Apple</span>
          </button>
          <Divider />
          <Field label="Email">
            <input style={S.input} type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && handleLogin()} className="auth-input" />
          </Field>
          <Field label="Password">
            <div style={{ position: "relative" }}>
              <input style={{ ...S.input, paddingRight: 44 }} type={showPass ? "text" : "password"} placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && handleLogin()} className="auth-input" />
              <button onClick={() => setShowPass(!showPass)} style={S.eyeBtn}>
                <svg viewBox="0 0 24 24" style={{ width: 16, height: 16, fill: "none", stroke: "#bbb", strokeWidth: 1.8 }}>
                  {showPass
                    ? <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></>
                    : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>}
                </svg>
              </button>
            </div>
          </Field>
          <div style={{ textAlign: "right", marginTop: 8, marginBottom: 28 }}>
            <span onClick={() => { setView("reset"); setError(""); }} style={S.link}>Forgot password?</span>
          </div>
          <button onClick={handleLogin} disabled={loading} style={S.btnDark} className="btn-dark-hover">
            {loading ? "Signing in..." : "Sign in"}
          </button>
          <p style={S.footer}>
            Don't have an account?{" "}
            <span onClick={onSignup} style={S.link}>Create one free</span>
          </p>
        </div>
      </div>

      <div style={S.right} className="auth-right-panel">
        <div style={{ width: "100%", maxWidth: 300 }}>
          <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.14em", color: "#bbb", marginBottom: 32, textTransform: "uppercase" }}>Playing worldwide</p>
          {[
            { title: "Blinding Lights", artist: "The Weeknd", active: true },
            { title: "As It Was", artist: "Harry Styles", active: false },
            { title: "Lay Phyu — အေးငြိမ်း", artist: "Iron Cross", active: false },
            { title: "Dynamite", artist: "BTS", active: false },
          ].map((t, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 0", borderBottom: "1px solid #f5f5f5" }}>
              <div style={{ width: 36, height: 36, borderRadius: 4, background: t.active ? "#000" : "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <svg viewBox="0 0 24 24" style={{ width: 12, height: 12, fill: t.active ? "#fff" : "#ccc" }}><polygon points="5 3 19 12 5 21 5 3"/></svg>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: t.active ? 600 : 400, color: t.active ? "#000" : "#aaa", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.title}</div>
                <div style={{ fontSize: 11, color: "#ccc", marginTop: 2 }}>{t.artist}</div>
              </div>
              {t.active && (
                <div style={{ display: "flex", gap: 2, alignItems: "flex-end", height: 16, flexShrink: 0 }}>
                  {[8, 14, 10, 16, 6].map((h, j) => (
                    <div key={j} style={{ width: 3, height: h, background: "#000", borderRadius: 2, animation: `eq${j} 0.8s ease infinite alternate` }} />
                  ))}
                </div>
              )}
            </div>
          ))}
          <div style={{ marginTop: 48, paddingTop: 32, borderTop: "1px solid #f0f0f0" }}>
            <p style={{ fontFamily: "'Instrument Serif', serif", fontStyle: "italic", fontSize: 20, color: "#000", lineHeight: 1.5, marginBottom: 8 }}>"Music connects every person on this planet."</p>
            <p style={{ fontSize: 12, color: "#ccc" }}>— The MyMusic mission</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Logo() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 48 }}>
      <img src="/logo192.png" alt="MyMusic" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover" }} />
      <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 600, letterSpacing: "-0.02em", color: "#000" }}>MyMusic</span>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: "block", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "#999", marginBottom: 8 }}>{label}</label>
      {children}
    </div>
  );
}

function Divider() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
      <div style={{ flex: 1, height: 1, background: "#f0f0f0" }} />
      <span style={{ fontSize: 11, color: "#ccc", fontWeight: 500, letterSpacing: "0.08em" }}>OR</span>
      <div style={{ flex: 1, height: 1, background: "#f0f0f0" }} />
    </div>
  );
}

function GoogleSVG() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48">
      <path fill="#EA4335" d="M24 9.5c3.1 0 5.9 1.1 8.1 2.9l6-6C34.5 3.5 29.5 1.5 24 1.5 14.8 1.5 7 7.3 3.7 15.3l7 5.4C12.4 14.1 17.7 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h12.7c-.6 3-2.3 5.5-4.9 7.2l7.6 5.9c4.4-4.1 7.1-10.1 7.1-17.1z"/>
      <path fill="#FBBC05" d="M10.7 28.8A14.4 14.4 0 0 1 9.5 24c0-1.7.3-3.3.8-4.8l-7-5.4A22.5 22.5 0 0 0 1.5 24c0 3.6.9 7 2.4 10l6.8-5.2z"/>
      <path fill="#34A853" d="M24 46.5c5.5 0 10.1-1.8 13.4-4.9l-7.6-5.9c-1.8 1.2-4.1 1.9-5.8 1.9-6.3 0-11.6-4.6-13.3-10.8l-6.8 5.2C7 39.2 14.8 46.5 24 46.5z"/>
    </svg>
  );
}

function AppleSVG() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="#000">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
    </svg>
  );
}

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:wght@300;400;500;600&display=swap');
  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
  body { background: #fff; }
  .auth-input { transition: border-color 0.15s; }
  .auth-input:focus { border-color: #000 !important; outline: none; }
  .auth-input::placeholder { color: #ccc !important; }
  .social-btn:hover { background: #f8f8f8 !important; }
  .btn-dark-hover:hover:not(:disabled) { opacity: 0.75; }
  .auth-right-panel { display: flex !important; }
  @media (max-width: 768px) { .auth-right-panel { display: none !important; } }
  @keyframes eq0 { from{height:8px} to{height:4px} }
  @keyframes eq1 { from{height:14px} to{height:8px} }
  @keyframes eq2 { from{height:10px} to{height:14px} }
  @keyframes eq3 { from{height:16px} to{height:6px} }
  @keyframes eq4 { from{height:6px} to{height:12px} }
`;

const S = {
  page: { minHeight: "100vh", background: "#fff", display: "flex", fontFamily: "'DM Sans', sans-serif" },
  left: { flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: "60px 40px", overflowY: "auto" },
  card: { width: "100%", maxWidth: 400 },
  right: { width: 440, background: "#fff", borderLeft: "1px solid #f0f0f0", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: "60px 48px" },
  heading: { fontFamily: "'Instrument Serif', serif", fontSize: 44, lineHeight: 1.05, letterSpacing: "-0.02em", color: "#000", marginBottom: 10 },
  sub: { fontSize: 14, color: "#999", lineHeight: 1.7, marginBottom: 36, fontWeight: 400 },
  errorBox: { background: "#fff5f5", border: "1px solid #fecaca", color: "#dc2626", padding: "12px 16px", borderRadius: 4, fontSize: 13, marginBottom: 20, textAlign: "center" },
  socialBtn: { width: "100%", padding: "13px 16px", borderRadius: 3, border: "1px solid #e5e5e5", background: "#fff", color: "#000", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 500, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 10, transition: "background 0.15s", letterSpacing: "-0.01em" },
  input: { width: "100%", padding: "13px 14px", borderRadius: 3, border: "1px solid #e5e5e5", background: "#fff", color: "#000", fontFamily: "'DM Sans', sans-serif", fontSize: 14, outline: "none" },
  eyeBtn: { position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#bbb", cursor: "pointer", display: "flex", alignItems: "center" },
  btnDark: { width: "100%", padding: "14px", borderRadius: 3, border: "none", background: "#000", color: "#fff", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer", letterSpacing: "0.02em", marginBottom: 24, transition: "opacity 0.15s" },
  btnOutline: { width: "100%", padding: "14px", borderRadius: 3, border: "1px solid #000", background: "transparent", color: "#000", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 500, cursor: "pointer" },
  btnGhost: { width: "100%", padding: "12px", borderRadius: 3, border: "none", background: "transparent", color: "#999", fontFamily: "'DM Sans', sans-serif", fontSize: 13, cursor: "pointer" },
  link: { color: "#000", fontWeight: 600, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 },
  footer: { textAlign: "center", fontSize: 13, color: "#999" },
};